/**
 * Bill of Lading Form Component
 *
 * The main form component for creating and editing Ocean Bills of Lading.
 * Combines all field sections into a single form. Mirrors OBForm patterns
 * exactly: forwardRef + useImperativeHandle to expose submit() for the
 * DocumentReviewer parent.
 *
 * Used in:
 * - B/L detail/review pages
 * - DocumentReviewer when documentType === 'ocean_bill_of_lading'
 *
 * Features:
 * - React Hook Form integration
 * - Issue-based highlighting for AI-extracted fields
 * - Validation
 * - Submit handling
 */

import { useForm } from 'react-hook-form';
import { useEffect, forwardRef, useImperativeHandle } from 'react';
import BLHeaderFields from './BLHeaderFields';
import BLPartiesFields from './BLPartiesFields';
import BLLocationsFields from './BLLocationsFields';
import BLCargoFields from './BLCargoFields';
import BLEquipmentFields from './BLEquipmentFields';
import BLTransportFields from './BLTransportFields';
import BLChargesFields from './BLChargesFields';
import BLClausesFields from './BLClausesFields';
import BLReferencesFields from './BLReferencesFields';
import Button from '../common/Button';
import { unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Default values for a new B/L form — all arrays empty, all scalars blank.
 * Having explicit defaults prevents react-hook-form uncontrolled-input warnings.
 */
const defaultValues = {
  header: {
    transportDocumentReference: '',
    transportDocumentTypeCode: '',
    documentStatus: '',
    version: '',
    carrierCode: '',
    carrierName: '',
    carrierCodeListProvider: '',
    issuingParty: '',
    issueDate: '',
    issuePlaceUNLocationCode: '',
    issuePlaceName: '',
    shippedOnBoardDate: '',
    receivedForShipmentDate: '',
    freightPaymentTermCode: '',
    originChargesPaymentTermCode: '',
    destinationChargesPaymentTermCode: '',
    serviceContractReference: '',
    contractQuotationReference: '',
    carrierBookingReference: '',
    shippingInstructionReference: '',
    receiptTypeAtOrigin: '',
    deliveryTypeAtDestination: '',
    cargoMovementTypeAtOrigin: '',
    cargoMovementTypeAtDestination: '',
    declaredValue: null,
    declaredValueCurrency: '',
    numberOfOriginals: null,
    numberOfCopies: null,
    preCarriageBy: '',
    incoTerms: '',
    termsAndConditions: ''
  },
  parties: [],
  shipmentLocations: [],
  consignmentItems: [],
  utilizedTransportEquipment: [],
  transportPlan: [],
  charges: [],
  totalCharges: {
    prepaidTotal: null,
    collectTotal: null,
    prepaidCurrency: '',
    collectCurrency: ''
  },
  clauses: [],
  amendments: [],
  references: []
};

/**
 * BLForm Component
 *
 * @param {Object} props - Component props
 * @param {Object} [props.billOfLading] - B/L data object (used in detail page)
 * @param {Object} [props.initialData] - Alias for billOfLading (used in DocumentReviewer)
 * @param {boolean} [props.isEditMode] - Whether the form is in edit mode
 * @param {Function} [props.onSave] - Called when form is submitted (detail page)
 * @param {Function} [props.onSubmit] - Alias for onSave (DocumentReviewer)
 * @param {Array} [props.issues] - Unified issues array from pipeline
 * @param {Object} [props.rawEvaluation] - LLM-as-judge evaluation result (may be double-nested)
 * @param {Object} [props.evaluation] - Alias for rawEvaluation (DocumentReviewer passes this)
 * @param {boolean} [props.disabled] - Whether the form is disabled (already approved)
 * @param {boolean} [props.hideActions] - Whether to hide the bottom action buttons
 * @param {boolean} [props.isSubmitting] - Show loading state on submit button
 * @param {string} [props.submitLabel] - Label for the submit button
 */
const BLForm = forwardRef(function BLForm({
  billOfLading,
  initialData,
  isEditMode = false,
  onSave,
  onSubmit: onSubmitProp,
  issues = [],
  rawEvaluation,
  evaluation: evaluationProp,
  disabled = false,
  hideActions = false,
  isSubmitting = false,
  submitLabel = 'Save'
}, ref) {
  // Support both prop naming conventions (detail page vs DocumentReviewer)
  const blData = billOfLading || initialData;
  const submitHandler = onSave || onSubmitProp;
  const evaluation = unwrapEvaluation(rawEvaluation || evaluationProp);

  // Merge B/L data with defaults to ensure all required fields exist
  const mergedDefaults = blData ? {
    ...defaultValues,
    ...blData,
    header: { ...defaultValues.header, ...(blData.header || {}) },
    parties: blData.parties || [],
    shipmentLocations: blData.shipmentLocations || [],
    consignmentItems: blData.consignmentItems || [],
    utilizedTransportEquipment: blData.utilizedTransportEquipment || [],
    transportPlan: blData.transportPlan || [],
    charges: blData.charges || [],
    totalCharges: { ...defaultValues.totalCharges, ...(blData.totalCharges || {}) },
    clauses: blData.clauses || [],
    amendments: blData.amendments || [],
    references: blData.references || []
  } : defaultValues;

  // Initialize react-hook-form with merged data
  const { control, register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: mergedDefaults,
    mode: 'onBlur'
  });

  // Reset form when data changes (same pattern as OBForm)
  useEffect(() => {
    reset(mergedDefaults);
  }, [JSON.stringify(blData)]);

  /**
   * Handle form submission — delegates to the parent-supplied handler
   */
  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitHandler?.(data);
    } catch (error) {
      console.error('BLForm submission error:', error);
    }
  });

  // Expose submit method to parent via ref (same pattern as OBForm / POForm)
  useImperativeHandle(ref, () => ({
    submit: () => {
      onSubmit();
    }
  }));

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* Header Section */}
      <BLHeaderFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
        evaluation={evaluation}
        errors={errors}
      />

      {/* Parties Section */}
      <BLPartiesFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Locations Section */}
      <BLLocationsFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Cargo Section — 3-level nested hierarchy */}
      <BLCargoFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Equipment Section */}
      <BLEquipmentFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Transport Plan Section */}
      <BLTransportFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Charges Section */}
      <BLChargesFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Clauses Section */}
      <BLClausesFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* References Section */}
      <BLReferencesFields
        control={control}
        register={register}
        watch={watch}
        isEditMode={isEditMode}
        issues={issues}
      />

      {/* Form Actions */}
      {isEditMode && !hideActions && (
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="submit"
            variant="success"
            size="lg"
            loading={isSubmitting}
            disabled={disabled}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
});

export default BLForm;
